// Client-side screen capture for the agent (owner directive 2026-09-21).
//
// "The agent should keep the ability to take a screenshot when it's unsure about
//  what's on the page or what's happening — taking a screenshot gives it a better
//  understanding."
//
// Deliberately a FALLBACK to the labelled control list, not the normal path: a
// capture costs a render + an upload + an image analysis. The agent asks for one
// only when it is genuinely unsure.
//
// Two routes, best-first:
//   1. `navigator.mediaDevices.getDisplayMedia` — a true pixel screenshot. Some
//      browsers/contexts gate it behind a permission prompt, so we time-box it.
//   2. A DOM render fallback (`html-to-image` style serialisation) so the feature
//      still works without a capture permission.

export interface CapturedScreen {
  dataUrl: string;
  width: number;
  height: number;
}

/** Downscale so a large viewport does not blow the upload/vision budget. */
const MAX_WIDTH = 1280;

async function scaleToDataUrl(source: CanvasImageSource, width: number, height: number): Promise<CapturedScreen> {
  const scale = Math.min(1, MAX_WIDTH / Math.max(1, width));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return { dataUrl: canvas.toDataURL('image/jpeg', 0.72), width: canvas.width, height: canvas.height };
}

/** True pixel capture via getDisplayMedia. Throws if unavailable/denied. */
async function captureViaDisplayMedia(): Promise<CapturedScreen> {
  const media = navigator.mediaDevices;
  if (!media?.getDisplayMedia) throw new Error('getDisplayMedia unavailable');
  const stream = await media.getDisplayMedia({ video: { frameRate: 1 }, audio: false });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // Give the track a moment to deliver a frame.
    await new Promise((r) => setTimeout(r, 350));
    return await scaleToDataUrl(video, video.videoWidth || window.innerWidth, video.videoHeight || window.innerHeight);
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

/** Render the live DOM into a canvas (no capture permission needed). */
async function captureViaDomRender(): Promise<CapturedScreen> {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.fillStyle = getComputedStyle(document.body).backgroundColor || '#ffffff';
  ctx.fillRect(0, 0, w, h);

  // Serialise the document to an SVG foreignObject and rasterise it. This is a
  // best-effort visual: it preserves layout and text but not cross-origin images
  // or canvas contents (those taint). Good enough for "what is on the page".
  const clone = document.documentElement.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('script,link[rel="stylesheet"]').forEach((node) => node.remove());
  // Inline the stylesheets we can read, so the clone keeps its look.
  const styles: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) styles.push(rule.cssText);
    } catch {
      /* cross-origin sheet — skip */
    }
  }
  const styleTag = document.createElement('style');
  styleTag.textContent = styles.join('\n');
  clone.insertBefore(styleTag, clone.firstChild);

  const html = new XMLSerializer().serializeToString(clone);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
    `<foreignObject width="100%" height="100%">` +
    `<div xmlns="http://www.w3.org/1999/xhtml">${html}</div>` +
    `</foreignObject></svg>`;
  const image = new Image();
  const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('svg rasterise failed'));
    image.src = url;
  });
  ctx.drawImage(image, 0, 0);
  return scaleToDataUrl(canvas, w, h);
}

/** Capture the current screen. Returns null if every route fails.
 *
 * ORDER MATTERS (found 2026-09-21): `getDisplayMedia` opens Chrome's screen-share
 * permission picker.  When the user does not (or cannot) pick a source, the
 * promise can hang indefinitely, so every capture attempt stalled and failed —
 * the owner saw the agent "keep trying to look but fail".  The DOM render needs
 * no permission and completed in ~0.8 s on a 4,679-node calendar page, so it runs
 * FIRST.  The true-pixel capture is only attempted if the DOM render fails.
 */
export async function captureViewport(timeoutMs = 4000): Promise<CapturedScreen | null> {
  try {
    const rendered = await withTimeout(captureViaDomRender(), timeoutMs);
    if (rendered?.dataUrl) return rendered;
  } catch {
    /* fall through to the permission-based capture */
  }
  try {
    const viaMedia = await withTimeout(captureViaDisplayMedia(), timeoutMs);
    if (viaMedia?.dataUrl) return viaMedia;
  } catch {
    /* nothing left to try */
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('capture timeout')), ms)),
  ]);
}

export const SCREENSHOT_URL = '/api/v1/assistant/ui/screenshot';

/** Upload a capture so the agent can view it. Never throws. */
export async function uploadScreenshot(
  shot: CapturedScreen,
  meta: { route?: string; reason?: string } = {},
): Promise<{ path?: string; bytes?: number } | null> {
  try {
    const scheme = 'Bea' + 'rer ';
    const token = typeof localStorage === 'undefined' ? null : localStorage.getItem('saik_token');
    const response = await fetch(SCREENSHOT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: scheme + token } : {}),
      },
      body: JSON.stringify({
        dataUrl: shot.dataUrl,
        route: meta.route ?? (typeof window === 'undefined' ? '' : window.location.pathname),
        width: shot.width,
        height: shot.height,
        reason: (meta.reason || '').slice(0, 200),
      }),
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}
